'use client';

import { SvgChevronright } from '@common/icon';
import { cn } from '@core/utils';
import Image from 'next/image';
import type { ComponentType } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { TERM_CATEGORY } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import {
  DONATION_ROUTES,
  ROUTES,
  SUB_DOMAIN,
  WEL_MARKET_ROUTES,
} from '@/constants';
import { useUserAgentMobile } from '@/hooks/useUserAgentMobile';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';

import {
  KosafHouseholdShortcutRow,
  type ShortcutActionRowProps,
} from './KosafHouseholdShortcutRow';
import { isExternalHref } from './MenuItems';
import { WelloPointRewardShortcutRow } from './WelloPointRewardShortcutRow';

type ShortcutAction = 'kosaf-household' | 'wello-point-reward';

//* 링크 이동이 아닌 클릭 핸들러가 필요한 항목을 매핑. 새 액션 추가 시 이 맵에만 등록.
const SHORTCUT_ACTION_COMPONENTS: Record<
  ShortcutAction,
  ComponentType<ShortcutActionRowProps>
> = {
  'kosaf-household': KosafHouseholdShortcutRow,
  'wello-point-reward': WelloPointRewardShortcutRow,
};

const ICON_BASE = 'https://cdn.welfarehello.com/static/shortcut';
const BUSINESS_INFO_HREF =
  'https://www.ftc.go.kr/bizCommPop.do?wrkr_no=7058602126';

interface ShortcutItem {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly iconSrc: string;
  readonly termsDrawerType?: TERM_CATEGORY;
  readonly actionType?: ShortcutAction;
  //* 모바일 디바이스(iOS/Android)에서만 노출. 데스크톱 웹에서는 숨김.
  //* 가구원 동의처럼 앱 스킴 리다이렉트가 필요해 데스크톱 의미가 없는 항목에 사용.
  readonly mobileOnly?: boolean;
}

interface ShortcutSection {
  readonly id: string;
  readonly title: string;
  readonly shortcutHref?: string;
  readonly items: readonly ShortcutItem[];
  readonly hiddenSubDomains?: readonly SUB_DOMAIN[];
}

const SECTIONS: readonly ShortcutSection[] = [
  {
    id: 'wello',
    title: '웰로',
    shortcutHref: ROUTES.HOME.pathname,
    items: [
      {
        id: 'custom-policy',
        label: '맞춤정책',
        href: ROUTES.RECOMMEND_POLICY_LIST.pathname,
        iconSrc: `${ICON_BASE}/custom-policy.svg`,
      },
      {
        id: 'hometown-news',
        label: '동네소식',
        href: ROUTES.SEARCH.withSearchParams({
          searchParams: { category: 'hometown-news' },
        }),
        iconSrc: `${ICON_BASE}/hometown-news.svg`,
      },
      {
        id: 'policy-guide',
        label: '정책 활용 가이드',
        href: ROUTES.SEARCH.withSearchParams({
          searchParams: { category: 'policy-info' },
        }),
        iconSrc: `${ICON_BASE}/policy-guide.svg`,
      },
      {
        id: 'hospital',
        label: '병원·응급실 찾기',
        href: ROUTES.RECOMMEND_POLICY_SITUATION_EMERGENCY_FORM.pathname,
        iconSrc: `${ICON_BASE}/hospital.svg`,
      },
    ],
  },
  {
    id: 'donation',
    title: '고향사랑기부',
    shortcutHref: DONATION_ROUTES.HOME.pathname,
    items: [
      {
        id: 'donation-info',
        label: '고향사랑기부 안내',
        href: DONATION_ROUTES.GUIDE.pathname,
        iconSrc: `${ICON_BASE}/notebook.svg`,
      },
      {
        id: 'gift',
        label: '답례품',
        href: DONATION_ROUTES.GIFTS.pathname,
        iconSrc: `${ICON_BASE}/gift.svg`,
      },
      {
        id: 'designated',
        label: '지정기부',
        href: DONATION_ROUTES.HOMETOWNS_BUSINESS.pathname,
        iconSrc: `${ICON_BASE}/designated.svg`,
      },
      {
        id: 'event',
        label: '이벤트',
        href: DONATION_ROUTES.MY_ETC_EVENT.pathname,
        iconSrc: `${ICON_BASE}/event.svg`,
      },
      {
        id: 'jeju-pass',
        label: '탐나는 제주패스',
        href: DONATION_ROUTES.MY_JEJU_RECOMMEND_INFO.pathname,
        iconSrc: `${ICON_BASE}/donation-info.svg`,
      },
    ],
  },
  {
    id: 'welmarket',
    title: '웰마켙',
    shortcutHref: WEL_MARKET_ROUTES.HOME.pathname,
    hiddenSubDomains: [SUB_DOMAIN.TOSS],
    items: [
      {
        id: 'point-market',
        label: '웰포인트 상점',
        href: ROUTES.POINT_MARKET.pathname,
        iconSrc: `${ICON_BASE}/point-market.svg`,
      },
    ],
  },
  {
    id: 'benefits',
    title: '혜택',
    items: [
      {
        id: 'wello-point-reward',
        label: '혜택',
        href: '#',
        iconSrc: '',
        actionType: 'wello-point-reward',
      },
      {
        id: 'attendance',
        label: '출석체크',
        href: ROUTES.ATTENDANCE_DAILY_CHECK.pathname,
        iconSrc: `${ICON_BASE}/attendance.svg`,
      },
      {
        id: 'insurance-refund',
        label: '연말정산 보험료 환급',
        href: 'https://blink.do/Wello',
        iconSrc: `${ICON_BASE}/insurance-refund.svg`,
      },
    ],
  },
  {
    id: 'useful-services',
    title: '유용한 서비스',
    items: [
      {
        id: 'benefit-notification',
        label: '행정안전부 혜택알리미',
        href: ROUTES.BENEFIT_NOTIFICATION_AGREEMENT.pathname,
        iconSrc: `${ICON_BASE}/benefit-notification.svg`,
      },
      {
        id: 'scholarship-report',
        label: '한국장학재단 학자금 채무자 신고',
        href: ROUTES.POLICY_CONSENT.pathname,
        iconSrc: `${ICON_BASE}/scholarship-report.svg`,
      },
      {
        id: 'scholarship-household',
        label: '한국장학재단 가구원 동의',
        href: '#',
        iconSrc: `${ICON_BASE}/scholarship-household.svg`,
        actionType: 'kosaf-household',
        mobileOnly: true,
      },
    ],
  },
  {
    id: 'policy-legal',
    title: '정책 및 법률',
    items: [
      {
        id: 'terms',
        label: '이용약관',
        href: '#',
        iconSrc: `${ICON_BASE}/note.svg`,
        termsDrawerType: TERM_CATEGORY.SERVICE_TERMS,
      },
      {
        id: 'user-info',
        label: '사용자정보확인',
        href: BUSINESS_INFO_HREF,
        iconSrc: `${ICON_BASE}/note.svg`,
      },
      {
        id: 'privacy',
        label: '개인정보처리방침',
        href: '#',
        iconSrc: `${ICON_BASE}/note.svg`,
        termsDrawerType: TERM_CATEGORY.PRIVACY_POLICY,
      },
      {
        id: 'marketing',
        label: '마케팅이용동의',
        href: '#',
        iconSrc: `${ICON_BASE}/note.svg`,
        termsDrawerType: TERM_CATEGORY.MARKETING_TERMS,
      },
      {
        id: 'location-terms',
        label: '위치기반서비스 이용약관',
        href: '#',
        iconSrc: `${ICON_BASE}/note.svg`,
        termsDrawerType: TERM_CATEGORY.LOCATION_POLICY_SELECT,
      },
    ],
  },
  {
    id: 'customer-service',
    title: '고객센터',
    items: [
      {
        id: 'notice',
        label: '공지사항',
        href: ROUTES.MY_NOTICE.pathname,
        iconSrc: `${ICON_BASE}/notice.svg`,
      },
      {
        id: 'faq',
        label: 'FAQ',
        href: ROUTES.MY_FAQ.pathname,
        iconSrc: `${ICON_BASE}/faq.svg`,
      },
    ],
  },
];

const ShortcutButton = ({
  href,
  onNavigate,
}: {
  href: string;
  onNavigate?: () => void;
}) => {
  const isExternal = isExternalHref(href);

  return (
    <CustomLink
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      onClick={onNavigate}
      className="flex w-60 items-center justify-center gap-4 rounded-xxsmall bg-Gray-100 py-4 transition-colors hover:bg-Gray-200"
    >
      <span className="font-600 text-Gray-900 typo-caption2">바로가기</span>
      <SvgChevronright className="size-10 text-Gray-700" />
    </CustomLink>
  );
};

const ShortcutRow = ({
  item,
  onNavigate,
  termsDrawerMode,
  closeOnTermsOpen = true,
}: {
  item: ShortcutItem;
  onNavigate?: () => void;
  termsDrawerMode?: 'default' | 'bottom';
  closeOnTermsOpen?: boolean;
}) => {
  const [setTermsDrawerType, setTermsDrawerMode] = useGlobalStore(
    useShallow(
      (state) =>
        [state.setTermsDrawerType, state.setTermsDrawerMode] as const,
    ),
  );
  const isExternal = isExternalHref(item.href);
  const handleTermsClick = () => {
    if (!item.termsDrawerType) return;

    setTermsDrawerMode(termsDrawerMode ?? 'default');
    setTermsDrawerType(item.termsDrawerType);
    if (closeOnTermsOpen) {
      onNavigate?.();
    }
  };

  if (item.actionType) {
    const ActionRow = SHORTCUT_ACTION_COMPONENTS[item.actionType];

    return (
      <ActionRow
        label={item.label}
        iconSrc={item.iconSrc}
        onNavigate={onNavigate}
      />
    );
  }

  if (item.termsDrawerType) {
    return (
      <button
        type="button"
        onClick={handleTermsClick}
        className="flex w-full items-center gap-8 px-20 py-12 text-left transition-colors hover:bg-Gray-50"
      >
        <Image
          src={item.iconSrc}
          alt=""
          width={24}
          height={24}
          unoptimized
          className="size-24 shrink-0"
        />
        <span className="line-clamp-1 font-500 text-Gray-900 typo-body3">
          {item.label}
        </span>
      </button>
    );
  }

  return (
    <CustomLink
      href={item.href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      onClick={onNavigate}
      className="flex items-center gap-8 px-20 py-12 transition-colors hover:bg-Gray-50"
    >
      <Image
        src={item.iconSrc}
        alt=""
        width={24}
        height={24}
        unoptimized
        className="size-24 shrink-0"
      />
      <span className="line-clamp-1 font-500 text-Gray-900 typo-body3">
        {item.label}
      </span>
    </CustomLink>
  );
};

export const ServiceShortcutPopoverContent = ({
  onNavigate,
  className,
  contentClassName,
  termsDrawerMode,
  closeOnTermsOpen = true,
}: {
  onNavigate?: () => void;
  className?: string;
  contentClassName?: string;
  termsDrawerMode?: 'default' | 'bottom';
  closeOnTermsOpen?: boolean;
}) => {
  const subDomain = useDeviceInfoStore((state) => state.subDomain);
  const isMobile = useUserAgentMobile();
  const sections = SECTIONS.filter(
    (section) => !subDomain || !section.hiddenSubDomains?.includes(subDomain),
  );

  return (
    <nav
      aria-label="서비스 바로가기"
      className={cn('py-32 duration-300 animate-in fade-in', className)}
    >
      <div className={cn('flex flex-col gap-28 px-20 pt-32', contentClassName)}>
        {sections.map((section) => (
          <section key={section.id} className="flex flex-col gap-12">
            {section.shortcutHref ? (
              <div className="flex items-center justify-between px-20">
                <h3 className="flex h-26 items-center font-700 text-Gray-900 typo-body3">
                  {section.title}
                </h3>
                <ShortcutButton
                  href={section.shortcutHref}
                  onNavigate={onNavigate}
                />
              </div>
            ) : (
              <h3 className="flex h-26 items-center px-20 font-700 text-Gray-900 typo-body3">
                {section.title}
              </h3>
            )}
            <ul className="flex flex-col">
              {section.items
                .filter((item) => !item.mobileOnly || isMobile)
                .map((item) => (
                  <li key={item.id}>
                    <ShortcutRow
                      item={item}
                      onNavigate={onNavigate}
                      termsDrawerMode={termsDrawerMode}
                      closeOnTermsOpen={closeOnTermsOpen}
                    />
                  </li>
                ))}
            </ul>
          </section>
        ))}
      </div>
    </nav>
  );
};
